import { useEffect, useState } from 'react';
import type { PortableBookEntry } from '@salonomy/types';
import { Avatar, Card, EmptyState, Pill, TextInput } from '@salonomy/ui';
import { usePortableBook } from '../../data/practitioner.js';
import { fullName, initials, maskPhone, money } from '../../lib/format.js';

/**
 * WS11 — "My book": the practitioner's OWN sealed clients, unioned by the server across every renter
 * residency they hold and TAGGED by the salon each row physically lives in (the read-side union —
 * ADR-0012 §2; the hook usePortableBook(search) is the only source of truth). This panel renders ONLY
 * the hook's rows: it never merges useClients (that is one tenant's book), never computes a client-side
 * union, and never derives the lifetime value — money() is a pence→£ display of the server figure.
 *
 * Search is debounced and fed straight to the hook, which filters the caller's own sealed rows server-
 * side. Each row carries the salonName Pill the server tagged it with; a private-practice leg reads
 * "Your studio" (cobalt). When the practitioner holds no sealed clients we show an honest EmptyState.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function PortableBookPanel() {
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 250);
  const { data: entries = [], isLoading } = usePortableBook(debouncedQ);

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">My book</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
            Your own sealed clients across every chair you rent, in one place. Each is tagged with
            the salon it lives in — no one else in those salons can see them.
          </div>
        </div>
        <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your book…"
          style={{ width: 220, borderRadius: 100, padding: '9px 16px' }}
        />
      </div>

      <div className="clients__list" style={{ marginTop: 12 }}>
        {isLoading && entries.length === 0 ? (
          <div className="muted" style={{ padding: 16 }}>
            Loading your book…
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            title={debouncedQ ? 'No clients match' : 'Your book is empty'}
            description={
              debouncedQ
                ? 'Try a different search.'
                : 'When you rent a chair with your own sealed book, the clients you build up there will appear here — yours to keep.'
            }
          />
        ) : (
          entries.map((e) => <BookRow key={`${e.salonId}:${e.clientId}`} entry={e} />)
        )}
      </div>
    </Card>
  );
}

function BookRow({ entry: e }: { entry: PortableBookEntry }) {
  const name = fullName(e.firstName, e.lastName);
  return (
    <div className="client-row" style={{ cursor: 'default' }}>
      <Avatar initials={initials(name)} tone="slate" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="client-row__name">{name}</div>
        <div className="client-row__sub">{maskPhone(e.phone)}</div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
        }}
      >
        <span className="mono" style={{ fontSize: 12 }}>
          {money(e.lifetimeValue)}
        </span>
        <Pill tone="cobalt" className="mono">
          {e.isPrivatePractice ? 'Your studio' : e.salonName}
        </Pill>
      </div>
    </div>
  );
}
